import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams, HttpParamsOptions, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

import { AppConfigService } from 'app/core/config/app-config.service';

import { map, tap } from 'rxjs/operators';

import { SortDirection } from '@angular/material/sort';

import { AuthRepository } from '@app/core/auth/auth.repository';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { ChecksheetChangeService } from 'app/modules/admin/academy/changes-academy.service';
import { StepAudioCaptureService } from './step-audio-capture.service';

/*
const HttpUploadOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Headers': '*',
  }),
};
*/

export interface StudentCourse {
  t_ot_student_courseid?: number;
  studentcourse_id?: number;
  course_id?: number;
  supervisor_id?: number;
  startdate?: Date;
  enddate?: Date;
  studentcourse_attest?: string;
  studentcourse_attest_date?: Date;
  supervisor_attest?: string;
  supervisor_attest_date?: Date;
  qualsec_attest?: string;
  qualsec_attest_date?: Date;
  ca?: string;
  ca_date?: Date;
  lang?: string;
  status_id?: number;
  created?: Date;
  created_by?: number;
  modified?: Date;
  modified_by?: number;
  info?: string;
  info1?: string;
  info2?: string;
  info3?: string;
  info4?: string;
  info5?: string;
  points?: number;
  certificate?: string;
  completion?: string;
  sort?: number;
  status2?: number;
  twinning?: number;
  uuid?: string;
  module?: string;
  badge?: string;
}

export interface StudentCourseSingle {
  data: StudentCourse;
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface StudentCourseTable {
  data: StudentCourse[];
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface StudentCourseStep {
  t_ot_student_course_stepid?: number; // Primary key
  step_id?: number;
  student_course_id?: number;
  completion_date?: Date;
  info?: string;
  mandatory?: number;
  student_input?: string;
  supervisor_answer?: string;
  status_id?: number;
  info1?: string;
  info2?: string;
  info3?: string;
  info4?: string;
  info5?: string;
  file?: string;
  twinning?: number;
  uuid?: string;
}

export interface Step {
  t_ot_stepid?: number;
  course_id?: number;
  section?: string;
  actiontype_id?: number;
  title?: string;
  duration?: string;
  description?: string;
  mandatory?: number;
  min_points?: number;
  points?: number;
  file?: string;
  file2?: string;
  file3?: string;
  file4?: string;
  file5?: string;
  info?: string;
  info1?: string;
  info2?: string;
  info3?: string;
  info4?: string;
  info5?: string;
  created?: Date;
  created_by?: number;
  modified?: Date;
  modified_by?: number;
  status_id?: number;
  sort?: number;
  protected?: number;
  twinning?: number;
  uuid?: string;
  ai?: number;
  ai_prompt?: string;
  ai_prompt_addon?: string;
  ai_type?: string;
  allow_print?: number;
  allow_copy?: number;
}

export interface StudentStepResponse {
  data: {
    student_step: StudentCourseStep;
    step: Step;
  };
  status: string;
  count: number;
  sec: string;
}

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    // 'Access-Control-Allow-Headers': '*',
    'Access-Control-Allow-Origin': '*',
  }),
};

//
// --------------------------------------------------------------
//

@Injectable({
  providedIn: 'root',
})
export class StudentCourseService {
  ACTION_TYPES = {
    1: { url: 'watch-video', caption: 'academy.stepType.watchVideo' },
    2: { url: 'read-text', caption: 'academy.stepType.readText' },
    3: { url: 'read-pdf', caption: 'academy.stepType.readPdf' },
    4: { url: 'write-essay', caption: 'academy.stepType.writeEssay' },
    5: { url: 'do-drill', caption: 'academy.stepType.doDrill' },
    6: { url: 'checkout', caption: 'academy.stepType.checkout' },
    7: { url: 'course-start', caption: 'academy.stepType.courseStart' },
    8: { url: 'completion', caption: 'academy.stepType.completion' },
    9: { url: 'drill-skype', caption: 'academy.stepType.drillSkype' },
    10: { url: 'clear-word', caption: 'academy.stepType.clearWord' },
    11: { url: 'read-policy', caption: 'academy.stepType.readPolicy' },
    12: { url: 'youtube', caption: 'academy.stepType.youtube' },
    13: { url: 'quiz', caption: 'academy.stepType.drill' },
    14: { url: 'final-test', caption: 'academy.stepType.certaintyDrill' },
    15: { url: 'how-are-you', caption: 'academy.stepType.howAreYou' },
    16: { url: 'twinning', caption: 'academy.stepType.twinning' },
    17: { url: 'supervisor', caption: 'academy.stepType.supervisor' },
    18: { url: 'gallery', caption: 'academy.stepType.practical' },
    19: { url: 'badge', caption: 'academy.stepType.badge' },
    20: { url: 'start-new-course', caption: 'academy.stepType.startNewCourse' },
    21: { url: 'phonetic-word', caption: 'academy.stepType.phoneticWord' },
    22: { url: 'video-slides', caption: 'academy.stepType.videoSlides' },
  };

  errorMsg: string = '';
  private recordingContextRevision = 0;
  private recordingRequestKey = '';

  constructor(
    private httpClient: HttpClient,
    private authRepository: AuthRepository,
    private notificationsService: NotificationsService,
    private checksheetChangeService: ChecksheetChangeService,
    readonly appConfig: AppConfigService,
    private stepAudio: StepAudioCaptureService,
  ) {}

  /**
   * GET ALL STUDENTCOURSE ENROLEMENTS -> STUDENTCOURSE COURSES
   *
   * @return response()
   */

  public getStudentEncrolements(student_id: number, student_course_id: number): Observable<any[]> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/course-enrolements/` + student_course_id;

    return this.httpClient.get<any[]>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'Access-Control-Allow-Headers': '*',
        'Access-Control-Allow-Origin': '*',
      }),
      params: new HttpParams().set('student_id', '' + student_id).set('student_course_id', '' + student_course_id),
    });
  }

  /**
   * GET THE CHECKSHEET OF THE GIVEN STUDENTCOURSE COURSE
   *
   * @return response()
   */

  public getStudentCourseChecksheet(student_course_id: number): Observable<any[]> {
    // Validate student_course_id - don't make API call for invalid IDs
    if (!student_course_id || student_course_id <= 0) {
      console.warn('getStudentCourseChecksheet: Invalid student_course_id:', student_course_id);
      return of([]); // Return empty array instead of making invalid API call
    }

    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/checksheet/` + student_course_id;

    return this.httpClient.get<any>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'Access-Control-Allow-Headers': '*',
        'Access-Control-Allow-Origin': '*',
      }),
      params: new HttpParams().set('student_course_id', '' + student_course_id),
    }).pipe(
      // The API always returns { status, data: [...], sec } — unwrap the array here
      // so every consumer (resolver → component) receives a plain array as promised.
      map((response: any) => Array.isArray(response?.data) ? response.data : []),
      catchError((error: HttpErrorResponse) => {
        // Handle duplicate UUID constraint violation (backend issue)
        if (error.error?.error?.includes('Duplicate entry') && error.error?.error?.includes('uuid')) {
          console.error('⚠️ Backend Error: Duplicate UUID detected when fetching checksheet', {
            student_course_id,
            error: error.error.error,
            message: 'The backend is trying to create records that already exist. This is a backend logic issue.'
          });
          
          // Return a user-friendly error message
          const errorMsg = 'Unable to load course steps. Please refresh the page or contact support if the issue persists.';
          this.errorMsg = errorMsg;
          
          // Try to return empty array or re-throw with better message
          return throwError(() => new Error(errorMsg));
        }
        
        // Handle other errors
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
        } else {
          errorMsg = this.getServerErrorMessage(error);
        }
        this.errorMsg = errorMsg;
        
        return throwError(() => new Error(errorMsg));
      })
    );
  }

  /**
   * ENROLL an existing student in a course
   * POST /v1/course-enrolements with { course_id, student_id, paid?, price_paid? }
   *
   * @param paid       When true, enrollment is created with status_id=2 (Paid, not yet started)
   * @param price_paid Actual amount charged (0 = free/gifted, >0 = paid). Internal analytics only.
   * @return response()
   */
  /**
   * Supervisor: set or clear the force-start override on a single enrollment.
   * When force_start is true the course bypasses prerequisite and study-path order locks.
   */
  public setForceStart(studentCourseId: number, value: boolean): Observable<any> {
    return this.httpClient.patch<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/force-start`,
      { force_start: value ? 1 : 0 }
    );
  }

  /**
   * Student activates a paid (status 2) enrollment → changes it to open (status 1).
   * The backend re-validates can_start (prerequisites / study path order) before allowing.
   */
  public activatePaidCourse(studentCourseId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/activate`,
      {}
    );
  }

  /**
   * Supervisor: set the valid-until date and/or the manual lock on one enrollment.
   * This is the most specific scope there is, so it overrides every policy layer
   * and is never recomputed. Pass access_until: null to clear the date.
   */
  public setCourseAccess(
    studentCourseId: number,
    payload: { access_until?: string | null; access_locked?: boolean; access_lock_reason?: string; reopen_days?: number }
  ): Observable<any> {
    return this.httpClient.patch<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/access`,
      payload
    );
  }

  /** Clear a manual lock and grant access for a fresh number of days. */
  public reopenCourseAccess(studentCourseId: number, days: number): Observable<any> {
    return this.setCourseAccess(studentCourseId, { reopen_days: days });
  }

  /** Discard a hand-set date and recompute from the current policy. */
  public reapplyCourseAccessPolicy(studentCourseId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/access/reapply-policy`,
      {}
    );
  }

  /**
   * Supervisor: let the student take the course again from scratch. Creates a new
   * enrollment with fresh steps and closes the current one, which stays visible
   * to the student as read-only history (certificate included).
   */
  public repeatCourse(studentCourseId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/repeat`,
      {}
    );
  }

  /** Remove an enrollment the student never opened (admins only). */
  public deleteEnrollment(studentCourseId: number): Observable<any> {
    return this.httpClient.delete<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}`
    );
  }

  /** Keep an unopened enrollment but revoke it as not paid (admins only). */
  public markEnrollmentUnpaid(studentCourseId: number): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/mark-unpaid`,
      {}
    );
  }

  /** Rendered enrollment invitation email for one enrollment (preview before resending). */
  public getInvitationEmail(studentCourseId: number): Observable<any> {
    return this.httpClient.get<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/invitation-email`
    );
  }

  /** Resend the invitation email; subject/html override the template for this send only. */
  public sendInvitationEmail(studentCourseId: number, edits: { subject?: string; html?: string } = {}): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.get('serverUrl')}/v1/student-courses/${studentCourseId}/invitation-email`,
      edits
    );
  }

  public enrollStudentInCourse(studentId: number, courseId: number, paid?: boolean, price_paid?: number): Observable<any> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/course-enrolements`;
    const data: any = {
      student_id: studentId,
      course_id: courseId,
    };
    if (paid) data.paid = 1;
    if (price_paid !== undefined) data.price_paid = price_paid;

    return this.httpClient.post<any>(apiUrl, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        return response?.data || response;
      }),
      catchError((error: HttpErrorResponse) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          errorMsg = `Error: ${error.error.message}`;
        } else {
          errorMsg = error.error?.error || this.getServerErrorMessage(error);
        }
        this.errorMsg = errorMsg;
        return throwError(() => new Error(errorMsg));
      })
    );
  }

  /**
   * SET DONE - checksheet step
   *
   * @return response()
   */
  public setChecksheetStepDone(student_step_id: number, dataIn: any): Observable<any> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/step-done/` + student_step_id;

    // fetch the company id and the student_id > from authStore

    const data = {
      timestamp: new Date().getTime(),
      //company_id: this.authRepository.getUser().company_id,
      student_id: this.authRepository.getUser().person_id,
      student_step_id: student_step_id,
      ...dataIn,
    };

    return this.httpClient.post<any>(apiUrl, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        if (response?.status !== 'OK' || !response.data) {
          throw new Error(response?.message || 'Could not save the step');
        }
        return {
          ...response.data,
          id: response.data.uuid,
          studentcourse: '',
        };
      }),
      tap((response: any) => {
        // Publish the persisted state before callers advance to another step.
        const statusId = Number(response?.status_id);
        if (Number.isFinite(statusId) && statusId > 0) {
          this.checksheetChangeService.updateData({
            current_step: {
              ...response,
              student_course_step_id: student_step_id,
              status_id: statusId,
            },
            status_update: true,
          });
        }
        this.checksheetChangeService.notifyMessageCountsChanged();
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  // Optionally, provide a method to get the action types
  getActionTypes() {
    return this.ACTION_TYPES;
  }

  // Optionally, provide a method to get a specific action type by ID
  getActionTypeById(id: number) {
    return this.ACTION_TYPES[id];
  }

  /**
   * Return the URL for the component that should show the specific step type (actiontypeid)
   */
  getActionRoute(actiontype_id: number): string {
    return this.ACTION_TYPES[actiontype_id]?.url || 'none';
  }

  /**
   * A nice printable form of the step-type (actiontype id)
   */
  getStepActionType(actiontype_id: number): string {
    return this.ACTION_TYPES[actiontype_id]?.caption || 'DO';
  }

  /*
    get the pdf for the pdf step
    */
  public getPdf(file: string): Observable<Blob> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/pdf`;

    return this.httpClient.get(apiUrl, {
      headers: new HttpHeaders({
        Accept: 'application/pdf',
      }),
      params: new HttpParams().set('f', file),
      responseType: 'blob',
    });
  }

  /**
   * GET THE CHECKSHEET STUDENT COURSE STEP
   *
   * @return response()
   */

  public getStudentCourseStep(student_course_id: number, step_id: number): Observable<StudentStepResponse> {
    const revision = ++this.recordingContextRevision;
    const navigation = this.stepAudio.contextRevision;
    const owner = this.authRepository.getUser()?.person_id;
    const requestKey = `${owner}:${student_course_id}:${step_id}`;
    if (requestKey !== this.recordingRequestKey) this.stepAudio.bindStep(null);
    this.recordingRequestKey = requestKey;
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/course-step/` + student_course_id + '/' + step_id;

    return this.httpClient.get<StudentStepResponse>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'Access-Control-Allow-Headers': '*',
        'Access-Control-Allow-Origin': '*',
      }),
      params: new HttpParams().set('student_course_id', '' + student_course_id).set('step_id', '' + step_id),
    }).pipe(tap(response => {
      if (revision === this.recordingContextRevision && navigation === this.stepAudio.contextRevision && owner === this.authRepository.getUser()?.person_id) this.stepAudio.bindStep(response.data?.student_step?.t_ot_student_course_stepid ?? null);
    }));
  }

  /**
   * GET ALL
   *
   * @return response()
   */

  public getStudentCourses(
    pageNumber: Number = 1,
    pageSize: Number = 20,
    sort: string = '',
    order: SortDirection = '',
    searchTerm: string = '',
    filtersArgs: string = ''
  ): Observable<StudentCourseTable> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/studentcourse`;
    // '?page=${pageNumber}&per_page=${pageSize}&sort=${sort}&sorder=${order}`

    return this.httpClient.get<StudentCourseTable>(apiUrl, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        // 'Access-Control-Allow-Headers': '*',
        'Access-Control-Allow-Origin': '*',
      }),
      params: new HttpParams()
        .set('page', '' + pageNumber)
        .set('per_page', '' + pageSize)
        .set('sort', sort)
        .set('sorder', order)
        .set('sterm', searchTerm)
        .set('filters', filtersArgs),
    });
  }

  /**
   * GET / FIND BY ID
   *
   * @return response()
   */
  getStudentcourseById(id: number): Observable<StudentCourseSingle> {
    const apiUrl = `${this.appConfig.get('serverUrl')}/v1/studentcourses/` + id;

    return this.httpClient
      .get<StudentCourseSingle>(apiUrl, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          // 'Access-Control-Allow-Headers': '*',
          'Access-Control-Allow-Origin': '*',
        }),
        params: new HttpParams().set('id', '' + id),
      })
      .pipe(
        map((response: StudentCourseSingle) => {
          return {
            ...response,
          };
        })
      );

    /*
    return this.httpClient.get<Studentcourse[]>(this.appConfig.get('serverUrl') + '/Studentcourse/' + id, httpOptions).pipe(
      tap((resp) => {
        //this.repo.setQuizes( resp || []);
        return resp;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
    */
  }

  /**
   * GET / FIND BY FILTER
   *
   * @return response()
   */
  getStudentcourseByParams(data: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    //const data: any = { this: 'thisThing', that: 'thatThing', other: 'otherThing'};

    const httpParams: HttpParamsOptions = { fromObject: data } as HttpParamsOptions;

    const options = { params: new HttpParams(httpParams), headers: headers };

    return this.httpClient.get<StudentCourse[]>(this.appConfig.get('serverUrl') + '/v1/studentcourses/', options).pipe(
      tap((resp) => {
        //this.repo.setQuizes( resp || []);
        return resp;
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * CREATE
   *
   * @return response()
   */
  createStudentcourse(post: any): Observable<any> {
    return this.httpClient.post<any>(this.appConfig.get('serverUrl') + '/v1/studentcourse/', JSON.stringify(post), httpOptions).pipe(
      map((response) => {
        return {
          ...response.data,
          id: response.data.uuid,
          studentcourse: '',
        };

        /*
          response.data.map((item) => ({
            ...item,
            id: item.quuid,
            quizdata: ''
          }));

          return response;
          */
      }),
      tap((response: any) => {
        console.log(response);
        //this.repo.addQuiz(response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * UPDATE
   *
   * @return response()
   */
  updateStudentcourse(studentcourse: StudentCourse): Observable<any> {
    let id = studentcourse.t_ot_student_courseid;
    const data = {
      //'id': quiz.qdata.id,
      uuid: '' + studentcourse.t_ot_student_courseid,
    };

    // do the update
    /*     return this.httpClient.post(this.appConfig.get('serverUrl') + '/v5/studentcourse/' + id, data, httpOptions)
      .pipe(
        catchError((res: HttpErrorResponse) => {
            console.log(res);
            return throwError(JSON.stringify(res));

        })
      );*/

    return this.httpClient.post<any>(this.appConfig.get('serverUrl') + '/v1/studentcourse/' + id, JSON.stringify(data), httpOptions).pipe(
      map((response) => {
        console.log('map');
        return {
          ...response.data,
          id: response.data.quuid,
          quizdata: '',
        };
      }),
      tap((response: any) => {
        console.log('tAP');
        //this.repo.updateQuiz(response);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * DELETE
   *
   * @return response()
   */
  deleteStudentcourse(id: string) {
    return this.httpClient.delete(this.appConfig.get('serverUrl') + '/v1/studentcourse/' + id, httpOptions).pipe(
      tap((value) => {
        //this.repo.removeQuiz(id);
      }),
      catchError((error) => {
        let errorMsg: string = '';
        if (error.error instanceof ErrorEvent) {
          this.errorMsg = `Error: ${error.error.message}`;
        } else {
          this.errorMsg = this.getServerErrorMessage(error);
        }

        return throwError(errorMsg);
      })
    );
  }

  /**
   * Write code on Method
   *
   * @return response()
   */
  private getServerErrorMessage(error: HttpErrorResponse) {
    console.log('custom service error handler');

    switch (error.status) {
      case 0: {
        // A client-side or network error occurred. Handle it accordingly.
        //console.error('An error occurred:', error.error);
        return `An error occurred: ${error.error}`;
      }
      case 404: {
        return `Not Found: ${error.message}`;
      }
      case 403: {
        return `Access Denied: ${error.message}`;
      }
      case 500: {
        return `Internal Server Error: ${error.message}`;
      }
      default: {
        return `Unknown Server Error: ${error.error}`;
      }

      // Return an observable with a user-facing error message.
      //return throwError(() => new Error('Something bad happened; please try again later.'));
    }
  }

  /**
   * Render the step approval/rejection email template server-side and return the HTML for preview.
   */
  public getStepEmailPreview(
    studentStepId: number,
    type: 'approval' | 'rejection',
    message: string
  ): Observable<{ subject: string; to: string; html: string }> {
    const params = new HttpParams().set('type', type).set('message', message);
    const url = `${this.appConfig.get('serverUrl')}/v1/step-done/${studentStepId}/email-preview`;
    return this.httpClient.get<any>(url, { params }).pipe(
      map((res) => res.data)
    );
  }
}
